import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authenticatedFetch } from '../services/authService';

type ConnectionStatus = 'connecting' | 'online' | 'offline';

interface ConnectionContextValue {
  status: ConnectionStatus;
  lastCheckedAt: number | null;
  lastOnlineAt: number | null;
  isInitialLoad: boolean;
  checkHealth: () => Promise<boolean>;
  setStatus: (s: ConnectionStatus) => void;
}

const ConnectionContext = createContext<ConnectionContextValue | undefined>(undefined);

export const ConnectionProvider: React.FC<{ children: React.ReactNode }>= ({ children }) => {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pollingRef = useRef<number | null>(null);

  const checkHealth = useCallback(async (): Promise<boolean> => {
    try {
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 4000);
      const res = await authenticatedFetch('/api/health', { credentials: 'include', signal: ctrl.signal });
      clearTimeout(to);
      setLastCheckedAt(Date.now());
      if (!res.ok) {
        return false;
      }
      const j = await res.json().catch(() => ({}));
      const ok = !!j && (j.ok === true || j.status === 'OK');
      if (ok) setLastOnlineAt(Date.now());
      return ok;
    } catch {
      setLastCheckedAt(Date.now());
      return false;
    }
  }, []);

  // Initial probe - wait longer during initial load to allow auth to complete
  useEffect(() => {
    let timeoutId: number | null = null;
    
    // Give more time during initial load (wait for auth to complete)
    const initialCheck = async () => {
      // Wait a bit before first check to allow auth to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const ok = await checkHealth();
      setStatus(ok ? 'online' : 'offline');
      
      // Mark initial load as complete after first check
      setIsInitialLoad(false);
    };
    
    initialCheck();
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [checkHealth]);

  // Background polling every 5s (only after initial load)
  useEffect(() => {
    if (isInitialLoad) return; // Don't poll during initial load
    
    if (pollingRef.current) window.clearInterval(pollingRef.current);
    pollingRef.current = window.setInterval(async () => {
      const ok = await checkHealth();
      setStatus(prev => {
        if (prev === 'offline' && ok) return 'online';
        if (prev === 'online' && !ok) return 'offline';
        return prev;
      });
    }, 5000) as unknown as number;
    return () => { if (pollingRef.current) window.clearInterval(pollingRef.current); };
  }, [checkHealth, isInitialLoad]);

  const value = useMemo(() => ({ status, lastCheckedAt, lastOnlineAt, isInitialLoad, checkHealth, setStatus }), [status, lastCheckedAt, lastOnlineAt, isInitialLoad, checkHealth]);
  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
};

export const useConnection = (): ConnectionContextValue => {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error('useConnection must be used within ConnectionProvider');
  return ctx;
};


